import { Box, Center, Flex, FlexProps, Text } from "@givenwell/components";
import { CSS, styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { createLink } from "@tanstack/react-router";
import { ComponentPropsWithoutRef, ForwardedRef, forwardRef, ReactNode } from "react";

export function BottomNav(props: FlexProps) {
  return (
    <>
      <Box
        css={{
          display: "block",
          backgroundImage: `linear-gradient(to bottom, ${colors.cream}00, ${colors.cream}40)`,
          width: "100%",
          position: "fixed",
          bottom: 0,
          paddingBottom: "calc(max(var(--safe-area-inset-bottom), 28px) - 12px)",

          pointerEvents: "none",
          "@lg": {
            display: "none",
          },
          ...props.css,
        }}
      >
        <Box
          css={{
            height: 64,
            "@xs": {
              height: 52,
            },
            "@sm": {
              height: 44,
            },
          }}
        />
      </Box>
      <Flex
        as="footer"
        {...props}
        css={{
          borderRadius: 9999,
          bg: colors.warmGreyHalf,
          boxShadow: "0 2px 24px rgba(0,0,0, .18), 0 0 0 1px hsla(0, 0%, 0%, 0.04)",
          zIndex: 1000,
          userSelect: "none",
          justifyContent: "center",
          position: "fixed",
          "--gap": "calc(max(var(--safe-area-inset-bottom), 28px) - 12px)",
          bottom: "var(--gap)",
          maxWidth: "calc(100% - var(--gap) - var(--gap))",
          left: "50%",
          transform: "translate(-50%, 0)",

          ...props.css,
          spaceX: -10,
          px: 4,
          "@xs": {
            "--gap": "calc(max(var(--safe-area-inset-bottom), 20px) - 12px)",
          },
          "@sm": {
            ...props.css?.["@sm"],
          },
          "@lg": {
            display: "none",
          },
        }}
      />
    </>
  );
}

type BottomNavItemProps = ComponentPropsWithoutRef<"button"> & {
  css?: CSS;
  className?: string;
  label: string;
  icon: ReactNode;
  activeIcon?: ReactNode;
  disabled?: boolean;
  badge?: ReactNode;
};

export const BottomNavItem = forwardRef(function BottomNavItem(
  { icon, activeIcon, label, badge, ...props }: BottomNavItemProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  return (
    <BottomNavItemRoot as="button" {...(props as any)} ref={ref}>
      <BottomNavItemContents {...{ icon, activeIcon, label, badge }} />
    </BottomNavItemRoot>
  );
});

export const BottomNavLink = createLink(
  forwardRef(function BottomNavLink(
    { icon, activeIcon, label, badge, ...props }: BottomNavItemProps,
    ref: ForwardedRef<HTMLAnchorElement>,
  ) {
    return (
      <BottomNavItemRoot as="a" {...(props as any)} ref={ref}>
        <BottomNavItemContents {...{ icon, activeIcon, label, badge }} />
      </BottomNavItemRoot>
    );
  }),
);

const BottomNavItemRoot = styled("div", {
  display: "flex",
  flexDir: "column",
  justify: "center",
  items: "center",
  position: "relative",
  flex: "1 0 0px",
  rounded: 9999,

  height: 62,
  width: 90,
  "@xs": {
    height: 52,
    width: 52,
  },
  "@sm": {
    flexDirection: "row",
    gap: 4,
    height: 44,
    width: 140,
  },

  "&[disabled]": {
    opacity: 0.5,
  },
  "&.active": {
    "&::before": {
      content: "''",
      display: "block",
      backgroundColor: colors.pinkAccent,
      position: "absolute",
      top: 4,
      bottom: 4,
      left: 0,
      right: 0,
      borderRadius: 9999,
      pointerEvents: "none",
    },
  },
});

function BottomNavItemContents({
  icon,
  activeIcon,
  label,
  badge,
}: {
  label: string;
  icon: ReactNode;
  activeIcon?: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <>
      <Center
        css={{
          color: colors.text2,
          ".active > &": {
            color: colors.navy,
          },
          position: "relative",
          size: 24,
          d: "flex",
          items: "center",
          justify: "center",
        }}
      >
        <Box
          css={{
            d: "block",
            ".active > * > &": {
              d: "none",
            },
          }}
        >
          {icon}
        </Box>
        <Box
          css={{
            d: "none",
            ".active > * > &": {
              d: "block",
            },
          }}
        >
          {activeIcon || icon}
        </Box>
        {badge}
      </Center>

      <Text
        css={{
          position: "relative",
          color: colors.text2,
          weight: 500,
          ".active > &": {
            color: colors.navy,
            weight: 600,
          },
          fontSize: 10,
          lineHeight: "16px",
          whiteSpace: "nowrap",
          "@xs": {
            d: "none",
          },
          "@sm": {
            fontSize: 12,
          },
        }}
      >
        {label}
      </Text>
    </>
  );
}
